import React, { useState, useEffect, useMemo } from 'react';
import { bookingService } from '../services/api';
import { generateColorForAsset } from '../utils/colorUtils';
import { sortBookings } from '../utils/sortUtils';
import { OPERATING_HOURS } from '../utils/constants';
import './BookingsList.css';

function BookingsList({
  user,
  assets,
  selectedAssetIds,
  setSelectedAssetIds,
  equipmentType,
  setEquipmentType,
  uniqueEquipmentTypes,
  allBookings: centralizedBookings,
  dataLoading,
  dataError,
  isSocketConnected,
  onRefreshData,
  modalState,
  openModal,
  closeModal,
  minimizeModal,
  expandModal
}) {
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('start');
  const [sortDirection, setSortDirection] = useState('asc');
  const [pastBookings, setPastBookings] = useState([]);
  const [pastCursor, setPastCursor] = useState(null);
  const [pastBookingsLoading, setPastBookingsLoading] = useState(false);
  const [hasMorePastBookings, setHasMorePastBookings] = useState(true);
  const [hasRequestedPastBookings, setHasRequestedPastBookings] = useState(false);

    // Process bookings from centralized data for selected assets
  const processedBookings = useMemo(() => {
    if (!centralizedBookings || selectedAssetIds.length === 0) return [];
    
    const bookingsMap = new Map(); // Use Map to prevent duplicates
    
    selectedAssetIds.forEach(assetId => {
      const assetBookings = centralizedBookings[assetId] || [];
      
      assetBookings.forEach(booking => {
        // Use booking ID as key to prevent duplicates
        if (!bookingsMap.has(booking.id)) {
          bookingsMap.set(booking.id, booking);
        }
      });
    });
    
    return Array.from(bookingsMap.values());
  }, [centralizedBookings, selectedAssetIds]);

  useEffect(() => {
    setPastBookings([]);
    setPastCursor(null);
    setPastBookingsLoading(false);
    setHasMorePastBookings(true);
    setHasRequestedPastBookings(false);
    setError(null);
  }, [selectedAssetIds]);

  // Update processedBookings when sort changes
  useEffect(() => {
    // No need for API calls - data comes from props
  }, [processedBookings.length, selectedAssetIds.length, isSocketConnected]);

  // Helper to get asset name from booking data (supports both populated names and fallback to asset lookup)
  const getAssetNameFromBooking = (booking, assetType) => {
    const nameField = `${assetType}Name`;
    const deletedField = `${assetType}Deleted`;
    const idField = `${assetType}Id`;
    
    // Check if booking is past and asset is deleted
    const isPast = booking.isPast || booking.extendedProps?.isPast;
    const isDeleted = booking.extendedProps?.[deletedField];
    
    // First try to get name from extendedProps (for bookings with populated asset data)
    if (booking.extendedProps?.[nameField]) {
      const assetName = booking.extendedProps[nameField];
      
      // Priority: deleted > unavailable
      if (isPast && isDeleted) {
        return `${assetName} - deleted`;
      }
      
      return assetName;
    }
    
    // Check if the asset is populated as an object in extendedProps
    const assetObj = booking.extendedProps?.[idField];
    if (assetObj && typeof assetObj === 'object' && assetObj.name) {
      const assetName = assetObj.name;
      const objDeleted = assetObj.isDeleted;
      
      // Priority: deleted > unavailable
      if (isPast && objDeleted) {
        return `${assetName} - deleted`;
      }
      
      return assetName;
    }
    
    // Fallback to asset lookup by ID for newer format
    const assetId = booking[idField] || booking.extendedProps?.[idField];
    if (assetId) {
      const asset = assets.find(a => a._id === assetId);
      if (asset) {
        // Priority: deleted > unavailable
        if (isPast && asset.isDeleted) {
          return `${asset.name} - deleted`;
        }
        if (!asset.available) {
          return `${asset.name} - unavailable`;
        }
        return asset.name;
      }
      return `${assetType.charAt(0).toUpperCase() + assetType.slice(1)} (ID: ${assetId})`;
    }
    
    return '-';
  };

  // Helper function to check if booking is in the past
  const isPastBooking = (booking) => {
    const now = new Date();
    const endDate = new Date(booking.end);
    return endDate < now || booking.isPast || booking.extendedProps?.isPast;
  };

  // Helper function to get CSS classes for asset name based on deletion status
  const getAssetNameClass = (booking, assetType) => {
    const deletedField = `${assetType}Deleted`;
    const idField = `${assetType}Id`;
    
    // Check if asset is deleted
    const isDeleted = booking.extendedProps?.[deletedField] || 
                     (booking.extendedProps?.[idField] && 
                      typeof booking.extendedProps[idField] === 'object' && 
                      booking.extendedProps[idField].isDeleted);
    
    // Only show deleted styling for past bookings
    return isPastBooking(booking) && isDeleted ? 'deleted-asset' : '';
  };
  
  // Handle column sort
  const handleSort = (field) => {
    const newDirection = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    // Sorting will be applied by useMemo when filteredBookings recomputes
  };

  // Format date for display (compact format)
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return { day, month };
  };
  
  // Format time range for display (stacked format)
  const formatTimeRange = (startStr, endStr) => {
    const startTime = new Date(startStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = new Date(endStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return { start: startTime, end: endTime };
  };
  
    // Handle row click - allow viewing past bookings in read-only mode
  const handleRowClick = (booking) => {
    const isPast = isPastBooking(booking);
    
    // Format booking object to match expected structure for BookingModal
    const formattedBooking = {
      id: booking.id,
      name: booking.title || booking.name,
      title: booking.title || booking.name,
      start: new Date(booking.start),
      end: new Date(booking.end),
      extendedProps: {
        userId: booking.extendedProps?.userId || booking.userId,
        wingId: booking.extendedProps?.wingId || booking.wingId || '',
        foilId: booking.extendedProps?.foilId || booking.foilId || '',
        boardId: booking.extendedProps?.boardId || booking.boardId || '',
        wingName: booking.extendedProps?.wingName,
        foilName: booking.extendedProps?.foilName,
        boardName: booking.extendedProps?.boardName,
        wingDeleted: booking.extendedProps?.wingDeleted,
        foilDeleted: booking.extendedProps?.foilDeleted,
        boardDeleted: booking.extendedProps?.boardDeleted,
        isPast: isPast
      }
    };
    
    openModal(isPast ? 'view' : 'edit', formattedBooking);
  };
  
  // Handler to open the booking creation modal
  const openCreateModal = () => {
    const primaryAssetIdForModal = selectedAssetIds.length > 0 ? selectedAssetIds[0] : null;
    const newBookingData = {
      // Default to current date/time
      start: new Date(),
      end: new Date(new Date().getTime() + OPERATING_HOURS.MIN_BOOKING_DURATION * 60000),
      assetId: primaryAssetIdForModal // For BookingModal default selection
    };
    openModal('create', newBookingData);
  };

  const extractUniqueBookings = (groupedBookings) => {
    const bookingsMap = new Map();

    Object.values(groupedBookings || {}).forEach(bookings => {
      bookings.forEach(booking => {
        if (!bookingsMap.has(booking.id)) {
          bookingsMap.set(booking.id, booking);
        }
      });
    });

    return Array.from(bookingsMap.values());
  };

  const mergeUniqueBookings = (existingBookings, newBookings) => {
    const mergedBookings = new Map();

    existingBookings.forEach(booking => mergedBookings.set(booking.id, booking));
    newBookings.forEach(booking => mergedBookings.set(booking.id, booking));

    return Array.from(mergedBookings.values()).sort(
      (a, b) => new Date(b.start) - new Date(a.start)
    );
  };

  const futureBookings = useMemo(() => {
    const now = new Date();
    if (selectedAssetIds.length === 0) {
      return [];
    }

    const future = processedBookings.filter(booking => {
      const endDate = new Date(booking.end);
      return !(endDate < now || booking.isPast || booking.extendedProps?.isPast);
    });

    return sortBookings(future, sortField, sortDirection);
  }, [processedBookings, selectedAssetIds, sortField, sortDirection]);

  // Combined bookings for display (past + future in chronological order)
  const filteredBookings = useMemo(() => {
    // Combine and sort all bookings chronologically
    // Past bookings are already sorted descending (most recent first)
    // Future bookings are sorted by sortField/sortDirection
    const combined = [...pastBookings, ...futureBookings];
    
    // Sort everything by start date to maintain chronological order
    return combined.sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return dateA - dateB; // Ascending chronological order
    });
  }, [futureBookings, pastBookings]);

  // Function to load more past bookings
  const loadMorePastBookings = async () => {
    if (selectedAssetIds.length === 0 || pastBookingsLoading || (hasRequestedPastBookings && !hasMorePastBookings)) {
      return;
    }

    setPastBookingsLoading(true);
    setError(null);

    try {
      const response = await bookingService.getPastBookings(selectedAssetIds, {
        limit: 10,
        beforeStart: pastCursor?.beforeStart || null,
        beforeId: pastCursor?.beforeId || null
      });

      const newlyLoadedBookings = extractUniqueBookings(response.data || {});
      setPastBookings(prev => mergeUniqueBookings(prev, newlyLoadedBookings));
      setPastCursor(response.meta?.nextCursor || null);
      setHasMorePastBookings(Boolean(response.meta?.hasMore));
      setHasRequestedPastBookings(true);
    } catch (err) {
      setError(err.message || 'Failed to load past bookings.');
    } finally {
      setPastBookingsLoading(false);
    }
  };

  // Show loading state
  if (dataLoading) {
    return <div className="loading-container"><h2>Loading Bookings...</h2></div>;
  }

  // Show error state
  if (dataError || error) {
    return <div className="error-message">{dataError || error}</div>;
  }

  return (
    <div className="list-main-content">
        <div className="bookings-header">
          <h1>All Bookings {selectedAssetIds.length > 0 ? `(${selectedAssetIds.length} equipment selected)` : '(No equipment selected)'}</h1>
          <div className="actions">
            <button className="book-button" onClick={openCreateModal}>
              Book Equipment
            </button>
          </div>
        </div>
        
        {/* Load Earlier Bookings Link */}
        {selectedAssetIds.length > 0 && (!hasRequestedPastBookings || hasMorePastBookings) && (
          <div className="load-past-bookings">
            <button type="button" onClick={loadMorePastBookings}>
              {pastBookingsLoading
                ? 'Loading Earlier Bookings...'
                : `Load Earlier Bookings${pastBookings.length > 0 ? ` (${pastBookings.length} past bookings shown)` : ''}`}
            </button>
          </div>
        )}
        
        {filteredBookings.length === 0 ? (
          <div className="no-bookings">
            {selectedAssetIds.length > 0 ? 'No upcoming bookings found for selected equipment.' : 'Select equipment from the sidebar to view bookings.'}
          </div>
        ) : (
          <div className="table-container">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('title')}>
                    User Name {sortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('start')}>
                    Date {sortField === 'start' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('start')}>
                    Time {sortField === 'start' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Wing</th>
                  <th>Foil</th>
                  <th>Board</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(booking => {
                  const isPast = isPastBooking(booking);
                  return (
                    <tr
                      key={booking.id}
                      onClick={() => handleRowClick(booking)}
                      className={`clickable-row ${isPast ? 'past-booking' : ''}`}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{booking.title}</td>
                      <td className="date-cell">
                        <div className="date-day">{formatDate(booking.start).day}</div>
                        <div className="date-month">{formatDate(booking.start).month}</div>
                      </td>
                      <td className="time-cell">
                        <div className="time-start">{formatTimeRange(booking.start, booking.end).start}</div>
                        <div className="time-end">{formatTimeRange(booking.start, booking.end).end}</div>
                      </td>
                      <td
                        className={getAssetNameClass(booking, 'wing')}
                        style={
                          (booking.wingId || booking.extendedProps?.wingId) && {
                            backgroundColor: generateColorForAsset(booking.wingId || booking.extendedProps?.wingId),
                            color: '#fff'
                          }
                        }
                      >
                        {getAssetNameFromBooking(booking, 'wing')}
                      </td>
                      <td
                        className={getAssetNameClass(booking, 'foil')}
                        style={
                          (booking.foilId || booking.extendedProps?.foilId) && {
                            backgroundColor: generateColorForAsset(booking.foilId || booking.extendedProps?.foilId),
                            color: '#fff'
                          }
                        }
                      >
                        {getAssetNameFromBooking(booking, 'foil')}
                      </td>
                      <td
                        className={getAssetNameClass(booking, 'board')}
                        style={
                          (booking.boardId || booking.extendedProps?.boardId) && {
                            backgroundColor: generateColorForAsset(booking.boardId || booking.extendedProps?.boardId),
                            color: '#fff'
                          }
                        }
                      >
                        {getAssetNameFromBooking(booking, 'board')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}

export default BookingsList;
